/**
 * Legacy endpoint type definitions
 * These types match the exact structure expected by the legacy admin UI
 */

/**
 * Anchor positions for endpoints in legacy format
 */
export type LegacyAnchor =
  'Left' | 'Right' | 'TopLeft' | 'TopRight' | 'BottomRight'

/**
 * UUID format for legacy endpoints: ${kind}-${nodeId}_${randomNumber}
 * Special case: start_node source endpoint uses "start_node-source"
 */
export type LegacyEndpointUuid = string

/**
 * Base endpoint structure
 */
export interface LegacyEndpoint {
  anchor: LegacyAnchor
  is_source: boolean
  uuid: LegacyEndpointUuid
  my_label: string
}

/**
 * Source endpoint (outgoing connection)
 */
export interface LegacySourceEndpoint extends LegacyEndpoint {
  is_source: true
  target_uuid: LegacyEndpointUuid | null
  option_id?: string // For dynamic handles (call_distribution, auto_attendant, interactive_webhook)
}

/**
 * Target endpoint (incoming connection)
 */
export interface LegacyTargetEndpoint extends LegacyEndpoint {
  is_source: false
  source_uuid: LegacyEndpointUuid | null
}

/**
 * Endpoints structure - every node must have both sources and targets arrays
 */
export interface LegacyEndpoints {
  sources: LegacySourceEndpoint[]
  targets: LegacyTargetEndpoint[]
}

/**
 * Type guard to check if an endpoint is a source endpoint
 */
export function isLegacySourceEndpoint(
  endpoint: LegacySourceEndpoint | LegacyTargetEndpoint,
): endpoint is LegacySourceEndpoint {
  return endpoint.is_source === true
}

/**
 * Type guard to check if an endpoint is a target endpoint
 */
export function isLegacyTargetEndpoint(
  endpoint: LegacySourceEndpoint | LegacyTargetEndpoint,
): endpoint is LegacyTargetEndpoint {
  return endpoint.is_source === false
}

/**
 * Validates UUID format: ${kind}-${nodeId}_${randomNumber}
 * Special cases:
 * - "start_node-source" is valid
 * - "${optionId}_src" is valid for option endpoints (auto_attendant, interactive_webhook, call_distribution)
 *
 * Note: Node kinds can contain underscores (e.g., "day_night", "auto_attendant"),
 * so we need to split from the end to separate the random number correctly.
 */
export function isValidLegacyEndpointUuid(uuid: string): boolean {
  if (uuid === 'start_node-source') {
    return true
  }

  // Special case: Option endpoints use format ${optionId}_src
  // Legacy code expects this format to identify option endpoints (edit_switchboard.php:590, 3926)
  // Option IDs can be in various formats (e.g., "auto_attendant-1768727726778_option-1768730104935_0")
  if (uuid.endsWith('_src')) {
    // Extract optionId (everything before _src)
    const optionId = uuid.substring(0, uuid.length - 4) // Remove '_src'
    // Option ID must be non-empty
    return optionId.length > 0
  }

  // Format: ${kind}-${nodeId}_${randomNumber}
  // Since kind can contain underscores (e.g., "day_night"), we need to split from the end
  const lastUnderscoreIndex = uuid.lastIndexOf('_')
  if (lastUnderscoreIndex === -1 || lastUnderscoreIndex === uuid.length - 1) {
    return false
  }

  const prefix = uuid.substring(0, lastUnderscoreIndex)
  const randomNumber = uuid.substring(lastUnderscoreIndex + 1)

  // Prefix must contain at least one dash (for kind-nodeId)
  if (!prefix.includes('-')) {
    return false
  }

  // Check that randomNumber is numeric and has appropriate length (10-13 digits)
  return /^\d+$/.test(randomNumber) && randomNumber.length >= 10
}

/**
 * Extracts kind from endpoint UUID
 * Format: ${kind}-${nodeId}_${randomNumber}
 */
export function extractKindFromUuid(uuid: string): string | null {
  if (uuid === 'start_node-source') {
    return 'start_node'
  }
  const parts = uuid.split('-')
  return parts[0] || null
}
